"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import { customerKundaliPath, type CustomerLanguage } from "@/lib/customer-language";

interface HouseAshtakDef {
  house: number;
  name: string;
  sanskrit: string;
  significance: string;
  idealBindus: string;
  highScoreEffect: string;
  lowScoreEffect: string;
  transitRule: string;
}

const HOUSES_ASHTAK: HouseAshtakDef[] = [
  {
    house: 1,
    name: "1st House (Lagna Bhava)",
    sanskrit: "तनु भाव (Self & Vitality)",
    significance: "Physical constitution, immunity, personality & charisma",
    idealBindus: "30+ Bindus",
    highScoreEffect:
      "Strong physical vitality, natural magnetic leadership, fast recovery from illness.",
    lowScoreEffect:
      "Vulnerable immunity; requires physical fitness discipline and healthy sleep schedule.",
    transitRule:
      "When Jupiter transits 1st house with 30+ bindus, personal reputation rises sharply.",
  },
  {
    house: 2,
    name: "2nd House (Dhana Bhava)",
    sanskrit: "धन भाव (Wealth & Speech)",
    significance: "Accumulated wealth, liquid savings, family lineage & speech",
    idealBindus: "28+ Bindus",
    highScoreEffect: "Effortless accumulation of savings, persuasive speech, family prosperity.",
    lowScoreEffect: "Fluctuating cash flow; tendency for money to slip away in impulsive spending.",
    transitRule:
      "Benefic transits through 2nd house with high bindus bring unexpected financial windfalls.",
  },
  {
    house: 3,
    name: "3rd House (Sahaja Bhava)",
    sanskrit: "सहज भाव (Courage & Siblings)",
    significance: "Personal courage, communication skills, siblings & short journeys",
    idealBindus: "26–28 Bindus",
    highScoreEffect:
      "High entrepreneurial risk tolerance, successful media/tech initiatives, supportive siblings.",
    lowScoreEffect:
      "Self-doubt in taking bold initiatives; requires structured execution planning.",
    transitRule:
      "Mars transiting 3rd house with 30+ bindus guarantees victory over business competitors.",
  },
  {
    house: 4,
    name: "4th House (Sukha Bhava)",
    sanskrit: "सुख भाव (Home & Assets)",
    significance: "Real estate, domestic happiness, mother's health & luxury vehicles",
    idealBindus: "30+ Bindus",
    highScoreEffect:
      "Owning multiple properties, peaceful domestic atmosphere, blessings from mother.",
    lowScoreEffect: "Frequent domestic relocations or disputes over fixed property assets.",
    transitRule:
      "Venus or Jupiter transiting 4th house with high points delivers property acquisition.",
  },
  {
    house: 5,
    name: "5th House (Putra Bhava)",
    sanskrit: "पुत्र भाव (Intellect & Progeny)",
    significance: "Creative genius, higher education, children & past-life merits (Purva Punya)",
    idealBindus: "28–32 Bindus",
    highScoreEffect:
      "Sharp speculative intellect, brilliant academic achievements, accomplished children.",
    lowScoreEffect: "Distractions in studies; delays or concerns regarding progeny milestones.",
    transitRule:
      "Jupiter transiting 5th house with high SAV points triggers auspicious child birth or promotion.",
  },
  {
    house: 6,
    name: "6th House (Shatru Bhava)",
    sanskrit: "शत्रु भाव (Enemies & Debt)",
    significance: "Overcoming competitors, clearing debts, daily workplace service & health",
    idealBindus: "Below 28 Bindus (Lower is better for enemies)",
    highScoreEffect:
      "Easily crushes rival competitors, litigation clears in favor, high daily work stamina.",
    lowScoreEffect:
      "Susceptible to seasonal infections and workplace disputes if Mars is afflicted.",
    transitRule:
      "Saturn transiting 6th house with high bindus resolves chronic loans and court disputes.",
  },
  {
    house: 7,
    name: "7th House (Kalatra Bhava)",
    sanskrit: "कलत्र भाव (Partnership)",
    significance: "Marriage, spouse traits, long-term business partnerships & public contracts",
    idealBindus: "28+ Bindus",
    highScoreEffect:
      "Harmonious marital partnership, highly cooperative spouse, profitable business contracts.",
    lowScoreEffect:
      "Frequent friction in partnerships; requires conscious patience in communication.",
    transitRule:
      "Jupiter transiting 7th house with 30+ points confirms favorable marriage finalization.",
  },
  {
    house: 8,
    name: "8th House (Ayur Bhava)",
    sanskrit: "आयु भाव (Transformation)",
    significance: "Longevity, sudden inheritance, unearned wealth & occult research",
    idealBindus: "Below 25 Bindus (Lower reduces sudden obstacles)",
    highScoreEffect:
      "Deep research capability, unexpected inheritance, high occult/spiritual maturity.",
    lowScoreEffect:
      "Sudden unexpected disruptions during malefic transits; needs protective kavach.",
    transitRule:
      "Saturn transiting 8th house with low bindus creates testing times (Ashtama Shani).",
  },
  {
    house: 9,
    name: "9th House (Bhagya Bhava)",
    sanskrit: "भाग्य भाव (Fortune & Dharma)",
    significance: "Divine luck, father's blessings, higher spiritual wisdom & foreign pilgrimages",
    idealBindus: "30+ Bindus (Critical for easy luck)",
    highScoreEffect:
      "Powerful divine luck, smooth progress in life, high spiritual standing and fatherly support.",
    lowScoreEffect:
      "Efforts require double the work as luck acts slowly; requires regular dharmic seva.",
    transitRule:
      "Sun or Jupiter transiting 9th house with 32+ bindus opens breakthrough opportunities.",
  },
  {
    house: 10,
    name: "10th House (Karma Bhava)",
    sanskrit: "कर्म भाव (Career & Fame)",
    significance: "Executive authority, professional status, government honors & public legacy",
    idealBindus: "30+ Bindus (Ideally higher than 11th house)",
    highScoreEffect:
      "Rapid career promotions, public authority, top executive ranking and high respect.",
    lowScoreEffect: "Frequent career domain changes or feeling stagnated in middle management.",
    transitRule:
      "Saturn or Sun transiting 10th house with 30+ points delivers peak career milestone.",
  },
  {
    house: 11,
    name: "11th House (Labha Bhava)",
    sanskrit: "लाभ भाव (Gains & Network)",
    significance: "Cash flow revenue, fulfillment of desires, wealthy friends & elder siblings",
    idealBindus: "32+ Bindus (Should exceed 10th house for surplus)",
    highScoreEffect:
      "Massive recurring cash inflows, influential social network, fulfillment of all ambitions.",
    lowScoreEffect: "Income matches expenses without large surplus accumulation.",
    transitRule:
      "Any planet transiting 11th house with high bindus yields maximum financial gains.",
  },
  {
    house: 12,
    name: "12th House (Vyaya Bhava)",
    sanskrit: "व्यय भाव (Expenses & Moksha)",
    significance: "Foreign settlements, spiritual liberation (Moksha), sleep quality & expenses",
    idealBindus: "Below 26 Bindus (Lower than 11th ensures savings)",
    highScoreEffect:
      "Success in foreign countries, deep meditative sleep, strong inclination toward charity.",
    lowScoreEffect:
      "High expenditures exceeding income if 12th house bindus exceed 11th house bindus.",
    transitRule:
      "Ketu or Jupiter in 12th house with low bindus brings spiritual awakening and peaceful sleep.",
  },
];

export default function AshtakvargaMatrixStudio({
  language = "en",
}: {
  language?: CustomerLanguage;
}) {
  const [selectedHouse, setSelectedHouse] = useState<number>(10);

  const houseData = useMemo<HouseAshtakDef>(() => {
    return HOUSES_ASHTAK.find((h) => h.house === selectedHouse) || HOUSES_ASHTAK[9];
  }, [selectedHouse]);

  const handleConsult = () => {
    const prompt = `Please analyze the Ashtakvarga points of my ${houseData.name} (${houseData.sanskrit}). How will current planetary transits affect my ${houseData.significance}?`;
    try {
      safeSessionSet("seo_entry_intent", "chart_report");
      safeSessionSet("seo_entry_path", "/ashtakvarga");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <HouseSelector selectedHouse={selectedHouse} onSelect={setSelectedHouse} />
      <HouseDetailCard houseData={houseData} onConsult={handleConsult} language={language} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Strength Matrix
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Sarvashtakavarga 12-House Bindu Strength Calculator (अष्टकवर्ग)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Select any of the 12 Kundali houses to evaluate its Sarvashtakavarga (SAV) points, strength
        thresholds, and transit predictions.
      </p>
    </div>
  );
}

function HouseSelector({
  selectedHouse,
  onSelect,
}: {
  selectedHouse: number;
  onSelect: (h: number) => void;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
        {HOUSES_ASHTAK.map((h) => {
          const isSelected = h.house === selectedHouse;
          return (
            <button
              key={h.house}
              type="button"
              onClick={() => onSelect(h.house)}
              className={`p-2.5 rounded-xl border text-center transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="block text-body-small font-bold text-text-primary">
                House {h.house}
              </span>
              <span className="block text-caption text-text-muted truncate">
                {h.sanskrit.split(" ")[0]}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HouseDetailCard({
  houseData,
  onConsult,
  language,
}: {
  houseData: HouseAshtakDef;
  onConsult: () => void;
  language: CustomerLanguage;
}) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/ashtakvarga"),
    "/ashtakvarga",
    language
  );

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {houseData.name} &middot; {houseData.sanskrit}
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-intent-brand/10 text-intent-brand">
              Ideal: {houseData.idealBindus}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Governs: <strong className="text-text-primary">{houseData.significance}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Check My House {houseData.house} Bindus ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-success/5 border border-success/20">
          <p className="text-caption font-bold uppercase tracking-wider text-success mb-1.5">
            High Bindu Score (30+ Points) Effects
          </p>
          <p className="text-caption text-text-primary leading-relaxed">
            {houseData.highScoreEffect}
          </p>
        </div>

        <div className="p-4 rounded-xl bg-warning/5 border border-warning/20">
          <p className="text-caption font-bold uppercase tracking-wider text-warning mb-1.5">
            Low Bindu Score (&lt;25 Points) Effects
          </p>
          <p className="text-caption text-text-primary leading-relaxed">
            {houseData.lowScoreEffect}
          </p>
        </div>
      </div>

      <div className="mt-4 p-4 rounded-xl bg-surface-raised border border-border-default">
        <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-1">
          Classical Transit Fructification Rule (गोचर फल)
        </p>
        <p className="text-caption text-text-secondary leading-relaxed">{houseData.transitRule}</p>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Calculate your exact 337 Sarvashtakavarga bindu distribution from your date and time of
          birth.
        </span>
        <Link
          href={`${customerKundaliPath(language)}/`}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Generate Full Kundali →
        </Link>
      </div>
    </div>
  );
}
